import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Leaf } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import ToneImg from "@/components/storefront/ToneImg";
import { Reveal } from "@/components/pharmacy/hooks";
import { ManagedReviews, ManagedInstagram } from "@/components/storefront/HomeSections";
import { K, MarketCard, MarketRow, MTitle, SeeMore, Stars, Discount, optionPills, priceText } from "@/components/market/MarketParts";
import { homeSets, discountPct } from "@/lib/homesets";
import { inr } from "@/lib/storeutils";

const wrap = "max-w-[1320px] mx-auto px-4 md:px-5";
const uniq = (list) => { const seen = new Set(); return list.filter((p) => p && !seen.has(p.id) && seen.add(p.id)); };

/** Days / hours / minutes / seconds left until the end of this week (Sunday night). */
function useWeekCountdown() {
  const calc = () => {
    const now = new Date();
    const end = new Date(now);
    end.setDate(now.getDate() + ((7 - now.getDay()) % 7));
    end.setHours(23, 59, 59, 999);
    const t = Math.max(0, Math.floor((end - now) / 1000));
    return { d: Math.floor(t / 86400), h: Math.floor((t % 86400) / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
  };
  const [left, setLeft] = useState(calc);
  useEffect(() => { const id = setInterval(() => setLeft(calc()), 1000); return () => clearInterval(id); }, []);
  return left;
}

export default function MarketHome() {
  const s = useStore();
  const cart = useCart();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();
  useEffect(() => { if (q && !s.loading) document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }, [q, s.loading]);

  const all = s.products;
  const sets = useMemo(() => homeSets(all, { fresh: 8, featured: 6, sale: 6 }), [all]);
  if (s.loading || !s.store) return <div className="min-h-screen flex items-center justify-center"><span className="w-10 h-10 rounded-full border-4 border-emerald-100 border-t-emerald-600 animate-spin" /></div>;
  if (s.error) return <div className="min-h-screen flex items-center justify-center text-slate-500">{s.error}</div>;

  const { slug, store } = s;
  const shown = all.filter((p) => `${p.name} ${p.category || ""}`.toLowerCase().includes(q));
  if (q) {
    return (
      <StoreShell>
        <section id="shop" className={`${wrap} py-10`} data-testid="market-search-results">
          <MTitle line>Results for “{q}”</MTitle>
          {shown.length === 0 ? <div className="text-center py-16" style={{ color: K.muted }}>No products found.</div> : (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="storefront-products">{shown.map((p) => <MarketCard key={p.id} p={p} s={s} cart={cart} />)}</div>
          )}
        </section>
      </StoreShell>
    );
  }

  const withImg = all.filter((p) => p.image);
  const ranked = uniq([...sets.fresh, ...sets.featured, ...sets.sale, ...withImg]).filter((p) => p.image);   // newest first: hero and banners show the latest products
  const pick = (i) => ranked[i % Math.max(ranked.length, 1)];
  const cats = s.categories.map((name) => ({ name, count: all.filter((p) => p.category === name).length, image: all.find((p) => p.category === name && p.image)?.image }));
  const prices = all.map((p) => p.price).filter((n) => n > 0);
  const maxOff = all.reduce((m, p) => Math.max(m, discountPct(p)), 0);
  const trending = sets.fresh.length ? sets.fresh : all.slice(0, 8);
  const deals = uniq([...sets.sale, ...sets.featured, ...all]).slice(0, 7);
  const onSell = uniq([...sets.sale, ...all.filter((p) => discountPct(p) > 0), ...all]).slice(0, 4);
  const best = uniq([...sets.featured, ...sets.fresh, ...all]).slice(0, 4);

  return (
    <StoreShell>
      <Hero s={s} items={[pick(0), pick(1), pick(2)]} price={pick(0)?.price ?? prices[0]} banners={[pick(3), pick(4), pick(5)]} maxOff={maxOff} />
      <CategoryStrip s={s} cats={cats} />
      <Featured s={s} cart={cart} all={all} featured={sets.featured} cats={cats} />
      <PromoStrip s={s} p={pick(6)} />
      {cats.length > 0 && <TopCategories s={s} cats={cats} />}
      {deals.length > 2 && <Deal s={s} cart={cart} deals={deals} spare={all.find((p) => p.image && !deals.some((d) => d.id === p.id))} />}
      <BannerTrio s={s} items={[pick(7), pick(8), pick(9)]} />
      {trending.length > 0 && (
        <section id="new-arrivals" className={`${wrap} py-8`}>
          <Reveal><MTitle line right={<SeeMore to={`/store/${slug}/shop`} />}>Trending Products</MTitle></Reveal>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="storefront-products">
            {trending.map((p, k) => <Reveal key={p.id} delay={(k % 4) * 80} kind="rv-zoom"><MarketRow p={p} s={s} cart={cart} /></Reveal>)}
          </div>
        </section>
      )}
      <WideBanner s={s} p={pick(10)} />
      <section id="bestsellers" className={`${wrap} py-8`}>
        <div className="grid lg:grid-cols-[1fr_1fr_1fr] gap-x-4 gap-y-8">
          <div className="lg:col-span-2">
            <Reveal><MTitle right={<SeeMore to={`/store/${slug}/shop?sort=price-asc`} />}>On Sell Products</MTitle></Reveal>
            <div className="mt-5 grid md:grid-cols-2 gap-4">
              {onSell[0] && <Reveal kind="rv-left"><BigCard p={onSell[0]} s={s} cart={cart} /></Reveal>}
              <div className="grid gap-4 content-start">
                {onSell.slice(1, 4).map((p, k) => <Reveal key={p.id} delay={k * 90} kind="rv-zoom"><MarketRow p={p} s={s} cart={cart} /></Reveal>)}
              </div>
            </div>
          </div>
          <div>
            <Reveal><MTitle right={<SeeMore to={`/store/${slug}/shop`} />}>Best Seller</MTitle></Reveal>
            <div className="mt-5 grid gap-4">
              {best.map((p, k) => <Reveal key={p.id} delay={k * 90} kind="rv-right"><MarketRow p={p} s={s} cart={cart} /></Reveal>)}
            </div>
          </div>
        </div>
      </section>
      <ManagedReviews s={s} layout="market" />
      <ManagedInstagram s={s} layout="market" />
    </StoreShell>
  );
}

/* ------------------------------------------------------- category strip */
function CategoryStrip({ s, cats }) {
  const ref = useRef(null);
  const [stuck, setStuck] = useState(false);
  // stays pinned just under the header while the page scrolls; gets a soft shadow once it is pinned
  useEffect(() => {
    const f = () => { const el = ref.current; if (el) setStuck(el.getBoundingClientRect().top <= 74); };
    f(); window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  if (!cats.length) return null;
  return (
    <section ref={ref} className="sticky top-[73px] z-20 mt-4 py-2 transition-shadow duration-300" style={{ background: "rgba(255,255,255,.94)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", boxShadow: stuck ? "0 8px 22px rgba(20,60,40,.1)" : "none" }} data-testid="market-cat-strip">
      <div className={wrap}>
        <div className="flex overflow-x-auto rounded-lg border bg-white" style={{ borderColor: K.line, scrollbarWidth: "none" }}>
          {cats.slice(0, 6).map((c, k) => (
            <Reveal key={c.name} delay={k * 70} className="flex-1 min-w-[170px]">
              <Link to={`/store/${s.slug}/shop?category=${encodeURIComponent(c.name)}`} data-testid={`market-cat-${c.name}`} className="mk-cat group flex items-center gap-3 px-4 py-2.5 border-r last:border-r-0 h-full transition-colors hover:bg-[#f3faf6]" style={{ borderColor: K.line }}>
                <span className="mk-cat-img w-10 h-10 rounded-full overflow-hidden shrink-0 border" style={{ borderColor: K.line, background: K.soft }}>{c.image ? <ToneImg src={c.image} loading="lazy" /> : <Leaf size={18} className="m-auto mt-3" style={{ color: K.primary }} />}</span>
                <span className="min-w-0"><span className="block text-[12.5px] font-semibold truncate" style={{ color: K.ink }}>{c.name}</span><span className="block text-[10.5px]" style={{ color: K.muted }}>{c.count} Product{c.count === 1 ? "" : "s"}</span></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ hero */
function PromoBanner({ s, p, bg, light = false, eyebrow, title, sub, price, badge, className = "", delay = 0, kind = "rv-zoom" }) {
  if (!p) return null;
  const fg = light ? "#fff" : K.ink;
  return (
    <Reveal kind={kind} delay={delay} className={`${className}`}>
      <Link to={`/store/${s.slug}/product/${p.id}`} className="mk-banner group relative block overflow-hidden rounded-xl h-full min-h-[150px]" style={{ background: bg }}>
        <span className="mk-shine" aria-hidden />
        <div className="relative z-10 p-5 md:p-6 max-w-[62%] h-full flex flex-col justify-center">
          {eyebrow && <div className="text-[11px] font-semibold opacity-80" style={{ color: fg }}>{eyebrow}</div>}
          <h3 className="text-[17px] md:text-[20px] font-extrabold leading-tight line-clamp-2" style={{ color: fg }}>{title}</h3>
          {sub && <div className="text-[11px] mt-1 opacity-80" style={{ color: fg }}>{sub}</div>}
          {price && <div className="mt-2 text-[20px] font-extrabold" style={{ color: light ? "#fff" : K.red }}>{price}</div>}
          <span className="mk-btn-white mt-3 inline-flex items-center self-start h-8 px-4 rounded-md bg-white text-[11px] font-bold shadow-sm" style={{ color: K.ink }}>Shop Now</span>
        </div>
        <div className="absolute right-3 top-3 bottom-3 w-[40%] rounded-lg overflow-hidden mk-banner-img shadow-lg"><ToneImg src={p.image} alt={p.name} loading="lazy" imgClassName="mk-zoom" /></div>
        {badge && <span className="mk-ring absolute left-[48%] bottom-5 z-20 w-14 h-14 rounded-full flex flex-col items-center justify-center text-white text-[13px] font-extrabold leading-none" style={{ background: K.red }}>{badge}<span className="text-[9px] mt-0.5">OFF</span></span>}
      </Link>
    </Reveal>
  );
}

function Hero({ s, items, price, banners, maxOff }) {
  const [a, b, c] = items;
  const [bt, bl, br] = banners;
  return (
    <section className={`${wrap} pt-4`} data-testid="market-hero">
      <div className="grid lg:grid-cols-[1.42fr_1fr] gap-4">
        <Reveal kind="rv-left">
          <div className="mk-hero relative overflow-hidden rounded-xl min-h-[360px] md:min-h-[470px]" style={{ background: "linear-gradient(120deg,#fff9ec 0%,#fdf1d6 55%,#f6e3b4 100%)" }}>
            <span className="mk-blob absolute -left-16 -bottom-20 w-72 h-72 rounded-full" style={{ background: "rgba(27,138,90,.10)" }} />
            <span className="mk-blob absolute right-10 -top-24 w-64 h-64 rounded-full" style={{ background: "rgba(239,125,26,.12)", animationDelay: "-3s" }} />
            <Leaf className="mk-sway absolute left-[44%] top-8 opacity-30" size={44} style={{ color: K.primary }} />
            <Leaf className="mk-sway absolute left-[52%] bottom-10 opacity-25" size={34} style={{ color: K.primary, animationDelay: "-2s" }} />
            <div className="relative z-10 p-7 md:p-10 max-w-full sm:max-w-[56%] min-w-0 sm:min-w-[260px]">
              <div className="mk-rise"><span className="mk-tag inline-block px-3 py-1.5 text-[12px] md:text-[13px] font-bold text-white" style={{ background: K.red }}>100% Farm Fresh Food</span></div>
              <h1 className="mk-rise mt-4 text-[44px] md:text-[68px] leading-[1.02]" style={{ fontFamily: K.script, color: K.ink, animationDelay: ".12s" }}>Fresh Organic</h1>
              <div className="mk-rise mt-2 text-[20px] md:text-[26px] font-semibold" style={{ color: K.primary, animationDelay: ".24s" }}>Food For All</div>
              <div className="mk-rise mt-4 text-[28px] md:text-[38px] font-extrabold" style={{ color: K.ink, animationDelay: ".36s" }}>{inr(price)}</div>
              <div className="mk-rise mt-5" style={{ animationDelay: ".48s" }}>
                <Link to={`/store/${s.slug}/shop`} data-testid="market-hero-cta" className="mk-cta inline-flex items-center gap-2 h-11 px-7 rounded-md text-white text-[13px] font-bold" style={{ background: K.primary }}>Shop Now <ArrowRight size={15} /></Link>
              </div>
              <div className="sm:hidden mt-6 flex gap-3" aria-hidden>
                {[a, b, c].map((p, i) => p && <div key={i} className="mk-float w-[28%] aspect-square rounded-xl overflow-hidden border-[3px] border-white shadow-lg" style={{ transform: `rotate(${[-5, 4, -3][i]}deg)`, animationDelay: `${i * 0.9}s` }}><ToneImg src={p.image} loading="eager" /></div>)}
              </div>
            </div>
            <div className="absolute right-3 md:right-6 top-0 bottom-0 w-[50%] hidden sm:block" aria-hidden>
              {[[a, "0%", "5%", 270, -7, 0], [b, "40%", "28%", 250, 6, 1.2], [c, "2%", "56%", 220, -3, 2.4]].map(([p, l, t, w, rot, d], i) => p && (
                <div key={i} className="mk-float absolute rounded-2xl overflow-hidden border-4 border-white shadow-2xl" style={{ left: l, top: t, width: `min(${w}px, 48%)`, aspectRatio: "1", transform: `rotate(${rot}deg)`, animationDelay: `${d}s` }}>
                  <ToneImg src={p.image} loading="eager" />
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 gap-4 lg:grid-rows-2">
          <PromoBanner s={s} p={bt} className="col-span-2" bg="linear-gradient(120deg,#e8eef6,#d6e2f0)" eyebrow={bt?.category ? `100% ${bt.category}` : "100% Fresh"} title={`Premium ${bt?.name || "Selection"}`} price={bt ? priceText(bt) : ""} kind="rv-right" />
          <PromoBanner s={s} p={bl} className="" bg="linear-gradient(135deg,#1747c9,#3d7bf0)" light title={`New ${bl?.name || "Arrival"}`} sub="Top Quality Product" delay={120} />
          <PromoBanner s={s} p={br} className="" bg="linear-gradient(135deg,#fde4ec,#fbd0de)" title={br?.name || "Special Offer"} badge={maxOff >= 5 ? `${maxOff}%` : "15%"} delay={220} />
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------ featured rail */
function Featured({ s, cart, all, featured, cats }) {
  const [tab, setTab] = useState("All");
  const rail = useRef(null);
  const [paused, setPaused] = useState(false);
  const tabs = ["All", ...cats.slice(0, 3).map((c) => c.name)];
  const pool = tab === "All" ? uniq([...featured, ...all]).slice(0, 12) : uniq([...featured.filter((p) => p.category === tab), ...all.filter((p) => p.category === tab)]).slice(0, 12);
  const go = (d) => { const el = rail.current; if (!el) return; const next = el.scrollLeft + d * el.clientWidth * 0.8; el.scrollTo({ left: next < 0 ? el.scrollWidth : next >= el.scrollWidth - el.clientWidth - 4 && d > 0 ? 0 : next, behavior: "smooth" }); };
  useEffect(() => { if (paused) return undefined; const id = setInterval(() => go(1), 4500); return () => clearInterval(id); }); // eslint-disable-line react-hooks/exhaustive-deps
  if (!all.length) return null;
  return (
    <section id="featured" className={`${wrap} pt-8 pb-4`} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <Reveal>
        <MTitle right={
          <div className="flex items-center gap-4 text-[11px] font-semibold">
            <div className="flex gap-3 flex-wrap">{tabs.map((t) => <button key={t} type="button" onClick={() => setTab(t)} className="mk-tab transition-colors" style={{ color: tab === t ? K.primary : K.muted, borderBottom: `2px solid ${tab === t ? K.primary : "transparent"}` }}>{t}</button>)}</div>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => go(-1)} aria-label="Previous products" className="w-7 h-7 rounded border inline-flex items-center justify-center transition-colors hover:bg-[#eef7f1]" style={{ borderColor: K.line }}><ChevronLeft size={14} /></button>
              <button type="button" onClick={() => go(1)} aria-label="Next products" className="w-7 h-7 rounded border inline-flex items-center justify-center transition-colors hover:bg-[#eef7f1]" style={{ borderColor: K.line }}><ChevronRight size={14} /></button>
            </div>
          </div>}>Featured Products</MTitle>
      </Reveal>
      <div key={tab} ref={rail} className="mk-rail mt-5 flex gap-4 overflow-x-auto scroll-smooth pb-2" style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }} data-testid="market-featured">
        {pool.map((p, k) => (
          <div key={p.id} className="mk-in shrink-0 w-[calc(50%-8px)] sm:w-[calc(33.33%-11px)] lg:w-[calc(20%-13px)] xl:w-[calc(16.66%-14px)]" style={{ scrollSnapAlign: "start", animationDelay: `${k * 60}ms` }}><MarketCard p={p} s={s} cart={cart} /></div>
        ))}
        {pool.length === 0 && <div className="py-12 w-full text-center text-sm" style={{ color: K.muted }}>No products in this category yet.</div>}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------- promo strip */
function PromoStrip({ s, p }) {
  if (!p) return null;
  return (
    <section className={`${wrap} py-4`}>
      <Reveal kind="rv-zoom">
        <Link to={`/store/${s.slug}/product/${p.id}`} className="mk-banner group relative flex items-center justify-between gap-4 overflow-hidden rounded-xl px-5 md:px-10 min-h-[92px] md:min-h-[110px]" style={{ background: "linear-gradient(90deg,#fbeaf0,#f7f2f4 55%,#fde3ec)" }}>
          <span className="mk-shine" aria-hidden />
          <div className="relative z-10 min-w-0">
            <div className="text-[12px] md:text-[14px] font-semibold" style={{ color: K.ink }}>The brand New Collection</div>
            <div className="text-[15px] md:text-[22px] font-extrabold leading-tight line-clamp-2" style={{ color: K.ink }}>{p.name}</div>
          </div>
          <div className="relative z-10 hidden sm:flex items-center gap-4">
            <div className="text-[12px] font-bold leading-tight text-right" style={{ color: K.ink }}>Our Pricing<br />Start</div>
            <span className="mk-ring inline-flex items-center justify-center h-12 px-6 rounded-full text-white text-[16px] md:text-[18px] font-extrabold" style={{ background: "#1747c9" }}>{inr(p.price)}</span>
          </div>
          <div className="relative z-10 h-[78px] md:h-[96px] w-[78px] md:w-[96px] shrink-0 rounded-full overflow-hidden border-4 border-white shadow-lg mk-float"><ToneImg src={p.image} loading="lazy" /></div>
        </Link>
      </Reveal>
    </section>
  );
}

/* ------------------------------------------------------ top categories */
function TopCategories({ s, cats }) {
  const list = [...cats].sort((a, b) => b.count - a.count).slice(0, 4);
  return (
    <section className={`${wrap} py-6`}>
      <Reveal><MTitle line right={<SeeMore to={`/store/${s.slug}/shop`} />}>Top Categories</MTitle></Reveal>
      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {list.map((c, k) => (
          <Reveal key={c.name} delay={k * 90} kind="rv-zoom">
            <Link to={`/store/${s.slug}/shop?category=${encodeURIComponent(c.name)}`} className="mk-card group flex items-center gap-3 rounded-lg border bg-white overflow-hidden pr-3" style={{ borderColor: K.line, background: "#f6f8f7" }}>
              <span className="w-[74px] md:w-[96px] h-[64px] md:h-[72px] shrink-0 overflow-hidden" style={{ background: K.soft }}>{c.image && <ToneImg src={c.image} loading="lazy" imgClassName="mk-zoom" />}</span>
              <span className="min-w-0 py-2"><span className="block text-[12.5px] md:text-[13.5px] font-semibold truncate" style={{ color: K.ink }}>{c.name}</span>
                {k === 1 && <span className="block text-[10px]" style={{ color: K.primary }}>Featured</span>}
                <Stars id={c.name + "x"} size={10} count={false} className="mt-1" /></span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ----------------------------------------------------------- deal week */
function Deal({ s, cart, deals, spare }) {
  const t = useWeekCountdown();
  const [big, ...rest] = deals;
  const cell = (n, l) => (
    <div className="mk-tile w-[48px] md:w-[56px] rounded-md text-white text-center py-1.5 leading-none" style={{ background: K.red }}>
      <div key={n} className="mk-pop text-[16px] md:text-[18px] font-bold">{String(n).padStart(2, "0")}</div><div className="text-[9px] mt-1 opacity-90">{l}</div>
    </div>
  );
  return (
    <section id="deals" className={`${wrap} py-6`}>
      <Reveal kind="rv-zoom">
        <div className="mk-deal rounded-2xl p-4 md:p-6" style={{ border: `2px solid ${K.accent}`, background: "linear-gradient(180deg,#fffef4,#fffdf0)" }}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-[20px] md:text-[24px] font-bold" style={{ color: K.ink }}>Deal Of The Week</h2>
            <div className="flex gap-2" aria-label="Time left on the deals">{cell(t.d, "Days")}{cell(t.h, "Hrs")}{cell(t.m, "Mins")}{cell(t.s, "Secs")}</div>
          </div>
          <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-[1.05fr_.95fr_1fr_1fr] gap-4">
            <div className="relative rounded-xl overflow-hidden min-h-[240px] lg:min-h-[420px] lg:-mr-10" style={{ background: "linear-gradient(160deg,#d7f1e1,#9fdcb8)" }}>
              <span className="mk-blob absolute -left-10 -top-10 w-56 h-56 rounded-full" style={{ background: "rgba(255,255,255,.35)" }} />
              <span className="mk-blob absolute right-4 bottom-10 w-40 h-40 rounded-full" style={{ background: "rgba(27,138,90,.18)", animationDelay: "-2s" }} />
              <div className="absolute left-5 top-5 z-10">
                <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: K.primary }}>This week only</div>
                <div className="text-[22px] md:text-[26px] font-extrabold leading-tight" style={{ color: K.ink }}>Fresh deals,<br />picked for you</div>
              </div>
              <div className="mk-float absolute left-[8%] bottom-6 w-[78%] max-w-[300px] aspect-square rounded-3xl overflow-hidden border-4 border-white shadow-2xl" style={{ transform: "rotate(-5deg)" }}><ToneImg src={(spare || big).image} loading="lazy" /></div>
            </div>
            <Reveal kind="rv-zoom" className="relative z-10"><BigCard p={big} s={s} cart={cart} /></Reveal>
            {[rest.slice(0, 3), rest.slice(3, 6)].map((col, ci) => (
              <div key={ci} className="grid gap-4 content-start">{col.map((p, k) => <Reveal key={p.id} delay={(ci * 3 + k) * 80} kind="rv-right"><MarketRow p={p} s={s} cart={cart} /></Reveal>)}</div>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* Large product card (Deal of the week, On sell). */
function BigCard({ p, s, cart }) {
  const pills = optionPills(p);
  const soldOut = p.stock <= 0;
  return (
    <div className="mk-card group h-full flex flex-col rounded-lg border bg-white p-4" style={{ borderColor: K.line }}>
      <span className="text-[10.5px]" style={{ color: K.muted }}>{p.category}</span>
      <Link to={`/store/${s.slug}/product/${p.id}`} className="relative block mt-2 rounded-md overflow-hidden aspect-square flex-1 min-h-[200px]" style={{ background: "#f6f8f7" }}>
        {p.image && <ToneImg src={p.image} alt={p.name} loading="lazy" imgClassName="mk-zoom" />}
      </Link>
      {pills.length > 0 && <div className="mt-3 flex gap-1.5 flex-wrap">{pills.map((v) => <span key={v} className="text-[9.5px] px-2 py-[2px] rounded-full border" style={{ borderColor: K.line, color: K.muted }}>{v}</span>)}</div>}
      <div className="mt-2 flex items-center gap-2 flex-wrap"><span className="text-[15px] font-bold" style={{ color: K.ink }}>{priceText(p)}</span><Discount p={p} /></div>
      <Stars id={p.id} className="mt-1.5" />
      <Link to={`/store/${s.slug}/product/${p.id}`}><h3 className="mt-1.5 text-[12.5px] leading-snug line-clamp-2" style={{ color: K.ink }}>{p.name}</h3></Link>
      <Link to={`/store/${s.slug}/product/${p.id}`} className="mk-btn mt-3 h-9 rounded-full text-[11.5px] font-semibold inline-flex items-center justify-center gap-1.5" style={{ background: K.soft, color: K.primary }}>{soldOut ? "Sold out" : "View product"} <ArrowRight size={13} /></Link>
    </div>
  );
}

/* ---------------------------------------------------------- banner trio */
function TrioBanner({ s, p, bg, titleColor, title, price, delay, kind }) {
  if (!p) return null;
  return (
    <Reveal kind={kind} delay={delay}>
      <Link to={`/store/${s.slug}/product/${p.id}`} className="mk-banner group relative block overflow-hidden rounded-xl min-h-[170px]" style={{ background: bg }}>
        <span className="mk-shine" aria-hidden />
        <div className="relative z-10 p-5 md:p-6 max-w-[58%] h-full flex flex-col justify-center">
          <h3 className="text-[18px] md:text-[21px] font-extrabold leading-tight line-clamp-2 underline decoration-2 underline-offset-4" style={{ color: titleColor }}>{title}</h3>
          <div className="mt-3 text-[11px]" style={{ color: K.ink }}>Only</div>
          <div className="text-[22px] font-extrabold" style={{ color: K.ink }}>{price}</div>
          <span className="mk-btn-white mt-3 inline-flex items-center self-start h-8 px-4 rounded-md bg-white text-[11px] font-bold shadow-sm" style={{ color: K.ink }}>Shop Now</span>
        </div>
        <div className="absolute right-3 bottom-3 top-6 w-[40%] rounded-xl overflow-hidden shadow-xl mk-banner-img"><ToneImg src={p.image} alt={p.name} loading="lazy" imgClassName="mk-zoom" /></div>
      </Link>
    </Reveal>
  );
}

function BannerTrio({ s, items }) {
  const [a, b, c] = items;
  if (!a) return null;
  return (
    <section className={`${wrap} py-4`}>
      <div className="grid md:grid-cols-3 gap-4">
        <TrioBanner s={s} p={a} bg="linear-gradient(135deg,#4d96ff,#a9d1ff)" titleColor="#fff" title={a.name} price={priceText(a)} kind="rv-left" />
        <TrioBanner s={s} p={b} bg="linear-gradient(135deg,#f1f26a,#d5e63a)" titleColor={K.ink} title={b.name} price={priceText(b)} delay={120} />
        <TrioBanner s={s} p={c} bg="linear-gradient(135deg,#b58be6,#e6ccf7)" titleColor={K.ink} title={c?.name} price={c ? priceText(c) : ""} delay={240} kind="rv-right" />
      </div>
    </section>
  );
}

/* ---------------------------------------------------------- wide banner */
function WideBanner({ s, p }) {
  if (!p) return null;
  return (
    <section className={`${wrap} py-4`}>
      <Reveal kind="rv-zoom">
        <Link to={`/store/${s.slug}/shop`} className="mk-banner group relative flex items-center overflow-hidden rounded-xl min-h-[120px] md:min-h-[140px]" style={{ background: "linear-gradient(180deg,#f7f9f8 0%,#f1f5f3 55%,#c9a26e 55%,#b88b58 100%)" }}>
          <span className="mk-shine" aria-hidden />
          <div className="relative z-10 px-5 md:px-10 flex items-baseline gap-3 flex-wrap">
            <span className="text-[16px] md:text-[22px] font-extrabold" style={{ color: K.ink }}>Daily {p.category || "Grocery"}</span>
            <span className="text-[20px] md:text-[34px] leading-none" style={{ fontFamily: K.script, color: K.primary }}>Tuesday Night Weekend Deal!</span>
          </div>
          <div className="relative z-10 ml-auto mr-4 md:mr-16 flex items-center gap-4">
            <div className="mk-swing hidden sm:flex w-20 h-20 rounded-full flex-col items-center justify-center text-white text-center leading-tight" style={{ background: K.red }}>
              <b className="text-[20px]">{Math.max(15, Math.min(60, Math.round(offOf(p))))}%</b><span className="text-[8px] uppercase font-semibold">Discount<br />Limited Time</span>
            </div>
            <div className="w-[88px] h-[88px] md:w-[108px] md:h-[108px] rounded-xl overflow-hidden border-4 border-white shadow-xl mk-float"><ToneImg src={p.image} loading="lazy" /></div>
          </div>
        </Link>
      </Reveal>
    </section>
  );
}
const offOf = (p) => discountPct(p) || 15;
